
import React from 'react';
import { useCostEstimator } from '@/context/CostEstimatorContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface FeatureOption {
  value: string;
  label: string;
  description: string;
}

const featureOptions: FeatureOption[] = [
  { value: 'auth', label: 'User Authentication', description: 'Login, registration, and user management' },
  { value: 'logo', label: 'Logo Design', description: 'Professional logo design for your app' },
  { value: 'customUI', label: 'Custom UI/UX Design', description: 'Unique, branded interface design' },
  { value: 'chat', label: 'Real-time Chat', description: 'Instant messaging between users' },
  { value: 'payments', label: 'Payments/In-app Purchases', description: 'Process payments and handle transactions' },
  { value: 'gps', label: 'GPS/Maps Integration', description: 'Location-based features and mapping' },
  { value: 'backend', label: 'Backend with Database', description: 'Server and database infrastructure' },
  { value: 'adminPanel', label: 'Admin Panel', description: 'Dashboard for managing your app' },
  { value: 'apiIntegration', label: 'API Integration', description: 'Connect with external services' },
  { value: 'push', label: 'Push Notifications', description: 'Real-time alerts to users' },
  { value: 'offline', label: 'Offline Mode', description: 'App functionality without internet' },
  { value: 'cloudSync', label: 'Cloud Sync', description: 'Data synchronization across devices' }
];

const FeaturesStep: React.FC = () => {
  const { toggleFeature, options, goToNextStep, goToPreviousStep, COSTS } = useCostEstimator();

  return (
    <Card className="form-card">
      <CardHeader>
        <CardTitle>Which features do you need?</CardTitle>
        <CardDescription>Select the features for your application</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {featureOptions.map((feature) => (
            <div 
              key={feature.value}
              className={cn(
                "flex items-start space-x-3 border rounded-lg p-4 transition-all duration-300",
                options.features.includes(feature.value as any) 
                  ? "border-primary bg-primary/5" 
                  : "border-border"
              )}
            >
              <Checkbox 
                id={feature.value} 
                checked={options.features.includes(feature.value as any)}
                onCheckedChange={() => toggleFeature(feature.value as any)}
                className="mt-1"
              />
              <div className="grid gap-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor={feature.value} className="font-medium">
                    {feature.label}
                  </Label>
                  <span className="text-sm text-primary">
                    ${COSTS.features[feature.value].toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={goToPreviousStep}>
            Previous
          </Button>
          <Button onClick={goToNextStep}>
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeaturesStep;
